import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { waLink } from "@/components/OrderQrCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarDays, Check, X, CalendarClock, MessageCircle, FilePlus2, Loader2, ExternalLink } from "lucide-react";
import { selectCls } from "@/components/erp/ErpUi";
import { useWaMsg } from "@/lib/waTemplates";
import { BookingScheduler } from "@/components/BookingScheduler";

const TABS = [["proximos", "Próximos"], ["pendente", "Pendentes"], ["todos", "Todos"]];
const ST = { pendente: "text-amber-300 border-amber-700", confirmado: "text-green-300 border-green-700", recusado: "text-red-300 border-red-900", convertido: "text-sky-300 border-sky-800", cancelado: "text-zinc-400 border-zinc-700" };
const brDate = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit" });

function RescheduleDialog({ appt, onClose, onDone }) {
  const [d, setD] = useState(appt?.date || "");
  const [t, setT] = useState(appt?.time || "");
  const save = async () => {
    try { await api.patch(`/booking/appointments/${appt.id}`, { action: "reschedule", date: d, time: t }); toast.success("Remarcado"); onDone(); onClose(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={!!appt} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reschedule-dialog" className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-sm">
        <DialogHeader><DialogTitle>Remarcar</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <Input data-testid="reschedule-date" type="date" value={d} onChange={(e) => setD(e.target.value)} className="bg-zinc-900 border-zinc-800 min-w-0" />
          <Input data-testid="reschedule-time" type="time" value={t} onChange={(e) => setT(e.target.value)} className="bg-zinc-900 border-zinc-800 min-w-0" />
        </div>
        <Button data-testid="reschedule-save" onClick={save} className="bg-green-brand text-white font-bold">Salvar e avisar</Button>
      </DialogContent>
    </Dialog>
  );
}

function PickCustomerDialog({ appt, onClose, onPicked }) {
  const [list, setList] = useState(null);
  const [cid, setCid] = useState("");
  useEffect(() => { if (appt) { api.get("/customers").then(({ data }) => setList(data)).catch(() => setList([])); } }, [appt]);
  return (
    <Dialog open={!!appt} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="pick-customer-dialog" className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-sm">
        <DialogHeader><DialogTitle>Escolha o cliente</DialogTitle></DialogHeader>
        <p className="text-sm text-zinc-400">Não achamos cliente com o telefone {appt?.phone}. Escolha um cliente existente ou cadastre {appt?.name} em Clientes.</p>
        {!list ? <Loader2 className="h-4 w-4 animate-spin" /> : (
          <select data-testid="pick-customer-select" value={cid} onChange={(e) => setCid(e.target.value)} className={selectCls}>
            <option value="">Selecione…</option>
            {list.map((c) => <option key={c.id} value={c.id}>{c.name} {c.phone ? `· ${c.phone}` : ""}</option>)}
          </select>
        )}
        <div className="flex gap-2">
          <Button data-testid="pick-customer-confirm" disabled={!cid} onClick={() => onPicked(cid)} className="flex-1 bg-green-brand text-white font-bold">Converter</Button>
          <a href="/owner/customers" className="flex-1 inline-flex items-center justify-center gap-1 rounded-md border border-zinc-800 text-sm text-zinc-300"><ExternalLink className="h-4 w-4" /> Cadastrar</a>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ApptCard({ a, shopName, act, onReschedule, onConvert }) {
  const active = a.status === "pendente" || a.status === "confirmado";
  const waMsg = useWaMsg();
  const msg = waMsg("booking_confirm", { cliente: a.name, placa: a.plate, data: `${brDate(a.date)} às ${a.time} (${a.service_name})` });
  return (
    <div data-testid={`appt-${a.id}`} className="rounded-2xl border border-card2-border bg-card2 p-4 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div><div className="font-display text-xl font-black text-zinc-100">{brDate(a.date)} · {a.time}</div><div className="text-sm text-zinc-300">{a.service_name} <span className="text-zinc-500">· {a.duration} min</span></div></div>
        <span data-testid={`appt-status-${a.id}`} className={`text-[11px] uppercase font-bold border rounded-full px-2 py-0.5 ${ST[a.status]}`}>{a.status}</span>
      </div>
      <div className="text-sm text-zinc-400">{a.name} · {a.phone} · <span className="font-mono text-zinc-200">{a.plate}</span>{shopName ? <span className="text-amber-300"> · {shopName}</span> : null}</div>
      {a.note && <div className="text-xs text-zinc-500 italic">“{a.note}”</div>}
      <div className="flex flex-wrap gap-2 pt-1">
        {a.status === "pendente" && <Button data-testid={`appt-confirm-${a.id}`} size="sm" onClick={() => act(a, "confirm")} className="bg-green-brand text-white"><Check className="h-4 w-4 mr-1" /> Confirmar</Button>}
        {active && <Button data-testid={`appt-reschedule-${a.id}`} size="sm" variant="outline" onClick={() => onReschedule(a)} className="bg-zinc-900 border-zinc-800"><CalendarClock className="h-4 w-4 mr-1" /> Remarcar</Button>}
        {active && <Button data-testid={`appt-reject-${a.id}`} size="sm" variant="outline" onClick={() => act(a, "reject")} className="bg-zinc-900 border-red-900 text-red-300"><X className="h-4 w-4 mr-1" /> Recusar</Button>}
        <a data-testid={`appt-wa-${a.id}`} href={waLink(a.phone, msg)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 h-9 px-3 rounded-md bg-emerald-600 text-white text-sm"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
        {active && <Button data-testid={`appt-convert-${a.id}`} size="sm" variant="outline" onClick={() => onConvert(a)} className="bg-zinc-900 border-zinc-800"><FilePlus2 className="h-4 w-4 mr-1" /> Converter em OS</Button>}
        {a.order_id && <a href={`/owner/ordens/${a.order_id}`} className="inline-flex items-center h-9 px-3 text-sm text-sky-300 underline">Abrir OS</a>}
      </div>
    </div>
  );
}

export default function OwnerBookings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState("proximos");
  const [list, setList] = useState(null);
  const [resch, setResch] = useState(null);
  const [pick, setPick] = useState(null);
  const shopNames = user?.shop_id === "all" ? Object.fromEntries((user?.shops || []).map((s) => [s.id, s.name])) : {};
  const load = useCallback(() => { api.get("/booking/appointments", { params: { status: tab } }).then(({ data }) => setList(data)).catch((e) => toast.error(formatApiError(e))); }, [tab]);
  useEffect(() => { load(); }, [load]);
  const prefill = useMemo(() => {
    const st = location.state?.prefill;
    if (st) return st;
    const cid = params.get("customer_id");
    if (!cid) return null;
    return { customer: { id: cid, name: params.get("name") || "" }, plate: params.get("plate") || "", vehicle_label: params.get("vehicle_label") || "", date: params.get("date") || "", time: params.get("time") || "", service_name: params.get("service_name") || "" };
  }, [location.state, params]);
  const autoOpenNew = params.get("novo") === "1" && !prefill;
  useEffect(() => {
    if (prefill || autoOpenNew) {
      const next = new URLSearchParams(params); ["novo", "customer_id", "name", "plate", "vehicle_label", "date", "time", "service_name"].forEach((k) => next.delete(k));
      setParams(next, { replace: true });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (a, action) => {
    const reason = action === "reject" ? window.prompt("Motivo (opcional, vai no e-mail ao cliente):") : null;
    if (action === "reject" && reason === null) return;
    try { await api.patch(`/booking/appointments/${a.id}`, { action, reason }); toast.success(action === "confirm" ? "Confirmado" : "Recusado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const convert = async (a, customer_id) => {
    try { const { data } = await api.post(`/booking/appointments/${a.id}/convert`, { customer_id }); toast.success("OS criada"); navigate(`/owner/ordens/${data.order_id}`); }
    catch (e) { if (e?.response?.status === 409) setPick(a); else toast.error(formatApiError(e)); }
  };
  const slug = user?.shop?.slug;
  return (
    <div className="space-y-4 animate-fade-up" data-testid="owner-bookings">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs font-mono-caps text-zinc-500 uppercase"><CalendarDays className="h-4 w-4" /> Agenda</div>
          <h1 className="font-display text-3xl font-black uppercase text-zinc-100 mt-1">Agendamentos</h1>
          {slug && <div className="text-xs text-zinc-500 mt-1">Link para clientes: <a href={`/o/${slug}/agendar`} target="_blank" rel="noreferrer" className="underline">/o/{slug}/agendar</a> · configure em Oficina → Agendamento online</div>}
        </div>
        <div className="flex gap-1 rounded-xl border border-zinc-800 p-1">{TABS.map(([k, l]) => <button key={k} data-testid={`bookings-tab-${k}`} onClick={() => setTab(k)} className={`px-3 h-8 rounded-lg text-sm ${tab === k ? "bg-zinc-800 text-zinc-100" : "text-zinc-400"}`}>{l}</button>)}</div>
      </div>
      <BookingScheduler initialPrefill={prefill} autoOpenNew={autoOpenNew} />
      {!list ? <Loader2 className="h-6 w-6 animate-spin mx-auto text-green-brand" /> : list.length === 0 ? <div data-testid="bookings-empty" className="text-sm text-zinc-500 py-10 text-center">Nenhum agendamento aqui.</div> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">{list.map((a) => <ApptCard key={a.id} a={a} shopName={shopNames[a.shop_id]} act={act} onReschedule={setResch} onConvert={(x) => convert(x)} />)}</div>
      )}
      <RescheduleDialog key={resch?.id} appt={resch} onClose={() => setResch(null)} onDone={load} />
      <PickCustomerDialog appt={pick} onClose={() => setPick(null)} onPicked={(cid) => { const a = pick; setPick(null); convert(a, cid); }} />
    </div>
  );
}
